import { Link, type LinkProps } from '@tanstack/react-router';
import { ExternalLink, Monitor, Smartphone, Tablet, Users } from 'lucide-react';
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { LiveChatVisitor, LiveChatVisitorState } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useInboxLive } from '../hooks/use-inbox-live';
import { useLiveVisitors, type VisitorsScope } from '../hooks/use-live-visitors';
import { visitorTag } from '../lib/display';
import { minutesOnSite, SAFE_URL, visitorLocation, visitorName } from '../lib/visitor-display';
import { VISITOR_STATES } from '../lib/visitors-cache';

import { StartChatDialog, type StartChatTarget } from './start-chat-dialog';
import { VisitorDetailsSheet, type VisitorDetailsTarget } from './visitor-details-sheet';

const DEVICE_ICONS = { desktop: Monitor, tablet: Tablet, mobile: Smartphone } as const;

const STATE_DOT: Record<LiveChatVisitorState, string> = {
  chatting: 'bg-primary',
  active: 'bg-success',
  idle: 'bg-warning',
};

export interface VisitorsPanelProps {
  /** One store (the store's own inbox) or the org-wide list, maybe narrowed by the store filter. */
  scope: VisitorsScope;
  /** Store names for the badge on the org-wide list (from each row's `storeId`); omitted on a store's own inbox. */
  storeNames?: ReadonlyMap<string, string> | undefined;
  /** Narrow column next to an open thread: fewer columns. */
  compact?: boolean;
  threadLink: (conversation: { id: string; storeId: string }) => LinkProps;
}

function useMinuteClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

function Duration({ minutes }: { minutes: number }) {
  const { t } = useTranslation('conversations');
  if (minutes < 1) return <>{t('visitors.duration.justNow')}</>;
  if (minutes < 60) return <>{t('visitors.duration.minutes', { count: minutes })}</>;
  return (
    <>{t('visitors.duration.hours', { hours: Math.floor(minutes / 60), minutes: minutes % 60 })}</>
  );
}

function PageLink({ visitor }: { visitor: LiveChatVisitor }) {
  const label = visitor.page.title?.trim() ? visitor.page.title : visitor.page.url;
  if (!SAFE_URL.test(visitor.page.url)) return <span className="truncate">{label}</span>;
  return (
    <a
      href={visitor.page.url}
      target="_blank"
      rel="noopener noreferrer"
      title={visitor.page.url}
      className="inline-flex max-w-full items-center gap-1 truncate underline-offset-2 hover:underline"
    >
      <span className="truncate">{label}</span>
      <ExternalLink className="size-3 shrink-0" aria-hidden />
    </a>
  );
}

function VisitorRow({
  visitor,
  name,
  storeName,
  compact,
  now,
  threadLink,
  onStartChat,
  onDetails,
}: {
  visitor: LiveChatVisitor;
  name: string;
  storeName: string | undefined;
  compact: boolean;
  now: number;
  threadLink: VisitorsPanelProps['threadLink'];
  onStartChat: (target: StartChatTarget) => void;
  onDetails: (target: VisitorDetailsTarget) => void;
}) {
  const { t } = useTranslation('conversations');
  const format = useFormat();
  const details = () => onDetails({ storeId: visitor.storeId, visitorId: visitor.id, name });
  const DeviceIcon = visitor.device ? DEVICE_ICONS[visitor.device] : null;
  const location = visitorLocation(visitor);
  const conversation = visitor.conversation;

  const action =
    visitor.state === 'chatting' && conversation ? (
      <Button asChild size="sm" variant="outline">
        <Link {...threadLink({ id: conversation.id, storeId: visitor.storeId })}>
          {t('visitors.openChat', { number: conversation.number })}
        </Link>
      </Button>
    ) : (
      <Can permission="conversations.write">
        <Button
          size="sm"
          onClick={() => onStartChat({ storeId: visitor.storeId, visitorId: visitor.id, name })}
          aria-label={t('visitors.startChatWith', { name })}
        >
          {t('visitors.startChat')}
        </Button>
      </Can>
    );

  return (
    <TableRow
      data-visitor-id={visitor.id}
      className="cursor-pointer"
      onClick={(event) => {
        // the row opens the details; its own links and buttons keep their job
        if (!(event.target as HTMLElement).closest('a, button')) details();
      }}
    >
      <TableCell className="max-w-56">
        <div className="flex items-center gap-2">
          <span
            className={cn('size-2 shrink-0 rounded-full', STATE_DOT[visitor.state])}
            aria-hidden
          />
          <button
            type="button"
            onClick={details}
            aria-label={t('visitors.detailsFor', { name })}
            className="truncate text-left font-medium underline-offset-2 hover:underline"
          >
            {name}
          </button>
          {visitor.customerId ? (
            <Badge variant="secondary" className="shrink-0">
              {t('visitors.customer')}
            </Badge>
          ) : null}
        </div>
        {storeName ? (
          <p className="text-muted-foreground mt-0.5 truncate pl-4 text-xs">{storeName}</p>
        ) : null}
        {compact ? (
          <p className="text-muted-foreground mt-0.5 truncate pl-4 text-xs">
            <PageLink visitor={visitor} />
          </p>
        ) : null}
      </TableCell>
      {compact ? null : (
        <TableCell className="text-muted-foreground max-w-72">
          <PageLink visitor={visitor} />
        </TableCell>
      )}
      <TableCell className="whitespace-nowrap tabular-nums" title={format.utc(visitor.startedAt)}>
        <Duration minutes={minutesOnSite(visitor.startedAt, now)} />
      </TableCell>
      {compact ? null : (
        <>
          <TableCell className="tabular-nums">
            {t('visitors.visitsValue', { visits: visitor.visitsCount, pages: visitor.pageViews })}
          </TableCell>
          <TableCell className="whitespace-nowrap tabular-nums">
            {visitor.cartTotal ? format.money(visitor.cartTotal) : '—'}
          </TableCell>
          <TableCell>
            {DeviceIcon && visitor.device ? (
              <span className="inline-flex items-center gap-1.5">
                <DeviceIcon className="text-muted-foreground size-4" aria-hidden />
                {t(`visitors.device.${visitor.device}`)}
              </span>
            ) : (
              '—'
            )}
          </TableCell>
          <TableCell className="max-w-40 truncate">{location ?? '—'}</TableCell>
        </>
      )}
      <TableCell className="text-right">{action}</TableCell>
    </TableRow>
  );
}

/**
 * Shoppers on the storefront now (LC7, tawk.to's monitoring): chatting, active and idle, with the
 * page they are on, time on site, visits, cart, device and location; a row opens the visitor's
 * details with every page of the visit. Rows follow
 * `visitor.updated` frames; the list is refetched every 30 s because leaving sends nothing.
 */
export function VisitorsPanel({
  scope,
  storeNames,
  compact = false,
  threadLink,
}: VisitorsPanelProps) {
  const { t } = useTranslation('conversations');
  const live = useInboxLive();
  const visitors = useLiveVisitors(scope, live.polling);
  const now = useMinuteClock();
  const [target, setTarget] = useState<StartChatTarget | null>(null);
  const [details, setDetails] = useState<VisitorDetailsTarget | null>(null);
  const detailsRow = details
    ? (visitors.items.find(
        (visitor) => visitor.id === details.visitorId && visitor.storeId === details.storeId,
      ) ?? null)
    : null;

  const nameOf = (visitor: LiveChatVisitor) =>
    visitorName(visitor) ?? t('row.visitor', { tag: visitorTag(visitor.id) });
  const columns = compact ? 3 : 8;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-4 py-2 text-xs"
        aria-label={t('visitors.summary')}
      >
        {VISITOR_STATES.map((state) => (
          <span key={state} className="inline-flex items-center gap-1.5">
            <span className={cn('size-2 rounded-full', STATE_DOT[state])} aria-hidden />
            {t(`visitors.state.${state}`)}
            <span className="text-foreground font-medium tabular-nums">
              {visitors.counts[state]}
            </span>
          </span>
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {visitors.isPending ? (
          <div className="space-y-3 p-3">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : visitors.error ? (
          <div className="p-4">
            <ErrorState error={visitors.error} onRetry={visitors.refetch} />
          </div>
        ) : visitors.items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={Users}
              title={t('visitors.empty')}
              description={t('visitors.emptyHint')}
            />
          </div>
        ) : (
          <Table aria-label={t('visitors.label')}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('visitors.columns.visitor')}</TableHead>
                {compact ? null : <TableHead>{t('visitors.columns.page')}</TableHead>}
                <TableHead>{t('visitors.columns.time')}</TableHead>
                {compact ? null : (
                  <>
                    <TableHead>{t('visitors.columns.visits')}</TableHead>
                    <TableHead>{t('visitors.columns.cart')}</TableHead>
                    <TableHead>{t('visitors.columns.device')}</TableHead>
                    <TableHead>{t('visitors.columns.location')}</TableHead>
                  </>
                )}
                <TableHead className="text-right">
                  <span className="sr-only">{t('visitors.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            {VISITOR_STATES.map((state) => {
              const rows = visitors.items.filter((visitor) => visitor.state === state);
              if (rows.length === 0) return null;
              return (
                <Fragment key={state}>
                  <TableBody data-state={state}>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableHead
                        colSpan={columns}
                        scope="colgroup"
                        className="h-8 text-xs font-medium"
                      >
                        {t('visitors.group', {
                          state: t(`visitors.state.${state}`),
                          count: rows.length,
                        })}
                      </TableHead>
                    </TableRow>
                    {rows.map((visitor) => (
                      <VisitorRow
                        key={`${visitor.storeId}:${visitor.id}`}
                        visitor={visitor}
                        name={nameOf(visitor)}
                        storeName={storeNames?.get(visitor.storeId)}
                        compact={compact}
                        now={now}
                        threadLink={threadLink}
                        onStartChat={setTarget}
                        onDetails={setDetails}
                      />
                    ))}
                  </TableBody>
                </Fragment>
              );
            })}
          </Table>
        )}
      </div>
      <VisitorDetailsSheet
        target={details}
        live={detailsRow}
        threadLink={threadLink}
        onStartChat={setTarget}
        onOpenChange={(open) => {
          if (!open) setDetails(null);
        }}
      />
      <StartChatDialog
        target={target}
        onOpenChange={(open) => {
          if (!open) setTarget(null);
        }}
      />
    </div>
  );
}
